import { useEffect, useState, type ReactNode } from "react";
import { IDENTITY_CHANNELS, type IdentityChannel, type IdentityDTO } from "../../shared/types";
import { del, get } from "../lib/api";
import { List, ListRow } from "./List";
import { Button, ErrorText, SectionHead, timeAgo } from "./ui";

const CHANNEL_NAMES: Record<IdentityChannel, string> = {
  email: "Email",
  slack: "Slack",
  github: "GitHub",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  phone: "Phone",
  google: "Google",
};

/** How the platform knows an account is this person's. It is never typed in: every row was proven. */
const PROOF_NAMES: Record<string, string> = {
  signin: "signed in with it",
  slack_profile: "matched through their Slack profile",
  code: "confirmed with a one-time code",
  migrated: "recognised before channels were kept apart",
  directory: "set by an admin or a directory sync",
  meet_participant: "seen signed in to a Google Meet call",
};

/**
 * The channel accounts that reach a person. Every door finds the sender by (channel, account), so these are
 * what makes a Slack DM or an email land in the same conversation as their web chat. Read-only on purpose:
 * an account is only ever linked where the platform proved it, because a linked one acts with their access.
 */
export function ConnectedChannels({ userId, manage = false, title, hint }: { userId: string; manage?: boolean; title?: ReactNode; hint?: ReactNode }) {
  const [identities, setIdentities] = useState<IdentityDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const path = manage ? `/api/users/${userId}/identities` : "/api/me/identities";
  const load = () => get<IdentityDTO[]>(path).then(setIdentities, (err) => setError((err as Error).message));

  useEffect(() => {
    load();
  }, [path]);

  const missing = IDENTITY_CHANNELS.filter((c) => !(identities ?? []).some((i) => i.channel === c));

  return (
    <div className="space-y-3">
      <ErrorText error={error} />
      {identities === null ? (
        <>
          <SectionHead title={title} hint={hint} />
          <p className="text-sm text-muted">Loading…</p>
        </>
      ) : (
        <List
          title={title}
          hint={hint}
          empty="No channels connected yet."
          footer={
            missing.length > 0 && (
              <p className="text-xs text-muted">
                Not connected yet: {missing.map((c) => CHANNEL_NAMES[c]).join(", ")}. A channel connects itself the first time it is used and proven
                {manage ? "" : " — there is nothing to fill in here"}.
              </p>
            )
          }
        >
          {identities.map((i) => (
            <ListRow
              key={i.id}
              leading={<div className="w-20 text-xs font-medium text-muted">{CHANNEL_NAMES[i.channel]}</div>}
              title={<span className="truncate font-mono text-sm">{i.label || i.externalId}</span>}
              subtitle={
                <span className="text-xs text-faint">
                  {PROOF_NAMES[i.proof] ?? i.proof} · {timeAgo(i.provenAt)}
                  {i.lastSeenAt && ` · last used ${timeAgo(i.lastSeenAt)}`}
                </span>
              }
              trailing={
                manage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      setError(null);
                      try {
                        await del(`/api/users/${userId}/identities/${i.id}`);
                        await load();
                      } catch (err) {
                        setError((err as Error).message);
                      }
                    }}
                  >
                    Remove
                  </Button>
                )
              }
            />
          ))}
        </List>
      )}
    </div>
  );
}
